import { type ReactNode, useEffect, useState } from 'react';
import { canWriteStaffScope, isRestrictedStaff } from '../lib/staff-access.js';
import { Link, useParams } from 'react-router-dom';
import { i18n, staffFeatureAllowed } from '@wms/shared';
import { ApiError, api } from '../lib/api.js';
import { CreateSkuDialog } from '../components/CreateSkuDialog.js';
import { ProductConfigurator } from '../components/ProductConfigurator.js';
import { RestockDialog } from '../components/RestockDialog.js';
import { StocktakeDialog } from '../components/StocktakeDialog.js';
import { AdjustStockDialog } from '../components/AdjustStockDialog.js';
import { StockMovementsTable } from '../components/StockMovementsTable.js';
import { EditPartnerDialog } from '../components/EditPartnerDialog.js';
import { InvitePartnerUserDialog } from '../components/InvitePartnerUserDialog.js';
import { UserPasswordResetButton } from '../components/UserPasswordResetButton.js';
import { RecordPaymentDialog } from '../components/RecordPaymentDialog.js';
import { PartnerBillingDialog } from '../components/PartnerBillingDialog.js';
import { BillingAccountCard } from '../components/BillingAccountCard.js';
import { useSettings } from '../lib/settings-context.js';
import { useAuth } from '../lib/auth-context.js';
import { formatCents, formatDate, paidUntilTone, type PaidUntilTone } from '../lib/format.js';

const { t } = i18n;

interface PartnerDetail {
  id: string;
  code: string;
  name: string;
  status: 'ACTIVE' | 'ARCHIVED';
  defaultSlaWorkdays: number;
  allowOperatorOrders: boolean;
  placersSeeOrgOrders: boolean;
  contactPerson: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  billingAddress: string | null;
  vatId: string | null;
  notes: string | null;
  billingMode: 'PER_PALLET_DAY' | 'FIXED_MONTHLY';
  customRateCents: number | null;
  pricingTierId: string | null;
  pricingTier: { id: string; name: string; kind: string } | null;
  paidUntil: string | null;
  lastPaymentAt: string | null;
  skus: {
    id: string;
    code: string;
    type: 'PRODUCT' | 'COMPONENT';
    nameLt: string;
    nameEn: string | null;
  }[];
}

interface TierOpt {
  id: string;
  name: string;
  kind: 'PER_DAY' | 'FIXED_MONTHLY';
}

interface PaymentRow {
  id: string;
  amountCents: number;
  currency: string;
  paidAt: string;
  periodFrom: string | null;
  periodTo: string | null;
  reference: string | null;
}

interface PartnerUserRow {
  userId: string;
  email: string;
  displayName: string;
  status: string;
  roles: string[];
}

const TONE_CLASS: Record<PaidUntilTone, string> = {
  green: 'bg-green-100 text-green-900 border-green-300 dark:bg-green-900/40 dark:text-green-100',
  amber: 'bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-900/40 dark:text-amber-100',
  red: 'bg-red-100 text-red-900 border-red-300 dark:bg-red-900/40 dark:text-red-100',
  unknown: 'bg-muted text-muted-foreground border-border',
};

export function PartnerDetailPage() {
  const { id = '' } = useParams();
  const settings = useSettings();
  const { user } = useAuth();
  const isAdmin =
    (user?.roles ?? []).some((r) => r.role === 'ADMIN') && canWriteStaffScope(user, 'PARTNER', id);
  // Mirrors `sku.write` in rbac.matrix.ts — ADMIN and PARTNER_ADMIN shape the
  // catalogue. An ORDER_PLACER may search it but not add to it (WMS-46).
  const canWriteSkus =
    canWriteStaffScope(user, 'PARTNER', id) &&
    staffFeatureAllowed(user, 'skuCreate') &&
    (user?.roles ?? []).some((r) => r.role === 'ADMIN' || r.role === 'PARTNER_ADMIN');
  const canCreateSku =
    canWriteStaffScope(user, 'PARTNER', id) && staffFeatureAllowed(user, 'skuCreate');
  const [partner, setPartner] = useState<PartnerDetail | null>(null);
  const [partnerUsers, setPartnerUsers] = useState<PartnerUserRow[]>([]);
  const [tiers, setTiers] = useState<TierOpt[]>([]);
  const [payments, setPayments] = useState<PaymentRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [skuOpen, setSkuOpen] = useState(false);
  const [configuratorOpen, setConfiguratorOpen] = useState(false);
  const [restockOpen, setRestockOpen] = useState(false);
  const [stocktakeOpen, setStocktakeOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [billingOpen, setBillingOpen] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  async function assignTier(tierId: string) {
    setAssignError(null);
    try {
      await api(`/pricing-tiers/partner/${id}`, {
        method: 'PUT',
        body: { tierId: tierId || null },
      });
      setReloadKey((k) => k + 1);
    } catch (err) {
      setAssignError(
        err instanceof ApiError && err.code === 'pricing_kind_mismatch'
          ? t('pricing.assign.kind_mismatch')
          : t('common.error'),
      );
    }
  }

  async function onToggleArchive() {
    if (!partner) return;
    const archiving = partner.status === 'ACTIVE';
    if (archiving && !confirm(t('partner.archive.confirm'))) return;
    setArchiveBusy(true);
    setArchiveError(null);
    try {
      const action = archiving ? 'archive' : 'unarchive';
      await api(`/partners/${partner.id}/${action}`, { method: 'POST' });
      setReloadKey((k) => k + 1);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'partner_has_open_orders') {
        setArchiveError(t('partner.archive.has_open_orders'));
      } else {
        setArchiveError(t('common.error'));
      }
    } finally {
      setArchiveBusy(false);
    }
  }

  useEffect(() => {
    setPartner(null);
    api<PartnerDetail>(`/partners/${id}`)
      .then(setPartner)
      .catch((err: unknown) => setError(String(err)));
    api<PaymentRow[]>(`/partners/${id}/payments`)
      .then(setPayments)
      .catch(() => setPayments([]));
    api<PartnerUserRow[]>(`/partners/${id}/users`)
      .then(setPartnerUsers)
      .catch(() => setPartnerUsers([]));
  }, [id, reloadKey]);

  useEffect(() => {
    api<TierOpt[]>('/pricing-tiers')
      .then(setTiers)
      .catch(() => setTiers([]));
  }, []);

  if (error) return <div className="text-sm text-red-600">{error}</div>;
  if (!partner) return <div className="text-sm text-muted-foreground">{t('common.loading')}</div>;

  return (
    <section className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <Link to="/partners" className="text-xs text-muted-foreground hover:underline">
            ← {t('partner.list.title')}
          </Link>
          <h2 className="text-xl font-semibold mt-1">
            <span className="font-mono text-base text-muted-foreground mr-2">{partner.code}</span>
            {partner.name}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setEditOpen(true)}
            className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted"
          >
            {t('common.edit')}
          </button>
          <button
            type="button"
            onClick={() => setInviteOpen(true)}
            className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted"
          >
            {t('partner.invite.action')}
          </button>
          <button
            type="button"
            onClick={() => void onToggleArchive()}
            disabled={archiveBusy}
            className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
          >
            {partner.status === 'ACTIVE'
              ? t('partner.archive.action')
              : t('partner.unarchive.action')}
          </button>
          <Link
            to={`/partners/${partner.id}/low-stock`}
            className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted"
          >
            {t('stock.low.action')}
          </Link>
          <button
            type="button"
            onClick={() => setAdjustOpen(true)}
            disabled={!canWriteStaffScope(user, 'PARTNER', id)}
            className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted"
          >
            {t('stock.adjust.action')}
          </button>
          <button
            type="button"
            onClick={() => setRestockOpen(true)}
            disabled={!canWriteStaffScope(user, 'PARTNER', id)}
            className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted"
          >
            {t('stock.restock.action')}
          </button>
          {canWriteStaffScope(user, 'PARTNER', id) &&
            user?.roles.some((r) => r.role === 'ADMIN' || r.role === 'OPERATOR') && (
              <button
                type="button"
                className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted"
                onClick={() => setStocktakeOpen(true)}
              >
                {t('stocktake.title')}
              </button>
            )}
          {/* WMS-46: search the catalogue by components before adding anything,
              so an existing product isn't recreated under a new name. */}
          <button
            type="button"
            onClick={() => setConfiguratorOpen(true)}
            className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted"
          >
            {t('configurator.action')}
          </button>
          <button
            type="button"
            onClick={() => setSkuOpen(true)}
            disabled={!canCreateSku}
            className="rounded bg-primary text-primary-foreground px-3 py-1.5 text-sm"
          >
            {t('sku.create.action')}
          </button>
        </div>
      </div>

      {partner.status === 'ARCHIVED' ? (
        <div className="rounded-md border border-amber-400 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-900/30 dark:text-amber-100">
          {t('partner.archive.banner')}
        </div>
      ) : null}
      {archiveError ? (
        <div className="rounded-md border border-red-400 bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-200">
          {archiveError}
        </div>
      ) : null}

      {partnerUsers.length > 0 ? (
        <div className="rounded border border-border p-3">
          <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t('partner.users.title')}
          </h3>
          <ul className="space-y-2">
            {partnerUsers.map((u) => (
              <li
                key={u.userId}
                className="flex flex-wrap items-center justify-between gap-2 text-sm"
              >
                <span>
                  <span className="font-medium">{u.displayName}</span>
                  <span className="ml-2 text-muted-foreground">{u.email}</span>
                  <span className="ml-2 text-[10px] uppercase tracking-wide text-muted-foreground">
                    {u.roles.map((r) => t(`role.${r}`)).join(', ')}
                  </span>
                </span>
                {isAdmin && !isRestrictedStaff(user) ? (
                  <UserPasswordResetButton userId={u.userId} email={u.email} />
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="grid gap-3 md:grid-cols-2 text-sm">
        <Info label={t('partner.col.sla')}>
          {partner.defaultSlaWorkdays} {t('partner.col.sla_unit')}
        </Info>
        <Info label={t('partner.col.operator_orders')}>
          {partner.allowOperatorOrders ? t('common.yes') : t('common.no')}
        </Info>
        <Info label={t('customer.contact.person')}>{partner.contactPerson ?? '—'}</Info>
        <Info label={t('customer.contact.email')}>{partner.contactEmail ?? '—'}</Info>
        <Info label={t('customer.contact.phone')}>{partner.contactPhone ?? '—'}</Info>
        <Info label={t('customer.col.billing_address')}>
          {partner.billingAddress ? (
            <span className="whitespace-pre-line">{partner.billingAddress}</span>
          ) : (
            '—'
          )}
        </Info>
        <Info label={t('customer.col.vat_id')}>{partner.vatId ?? '—'}</Info>
        {partner.notes ? (
          <Info label={t('customer.col.notes')}>
            <span className="whitespace-pre-line">{partner.notes}</span>
          </Info>
        ) : null}
      </div>

      {/* ----- Storage billing (Stage 2) ----- */}
      <div className="rounded-md border border-border p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t('billing.partner.title')}
          </h3>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPaymentOpen(true)}
              className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted"
            >
              {t('customer.action.record_payment')}
            </button>
            <button
              type="button"
              onClick={() => setBillingOpen(true)}
              className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted"
            >
              {t('billing.preview.title')}
            </button>
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-2 text-sm">
          <Info label={t('customer.col.billing_mode')}>
            {t(`customer.billing.${partner.billingMode}`)}
          </Info>
          {partner.customRateCents != null ? (
            <Info label={t('billing.custom_rate.label')}>
              {formatCents(partner.customRateCents)}
              {partner.billingMode === 'FIXED_MONTHLY'
                ? ` / ${t('pricing.per_month')}`
                : ` / ${t('pricing.per_day')}`}
            </Info>
          ) : null}
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              {t('customer.col.pricing_tier')}
            </div>
            <select
              value={partner.pricingTierId ?? ''}
              onChange={(e) => void assignTier(e.target.value)}
              className="mt-0.5 w-full rounded border border-border bg-background px-2 py-1.5 text-sm"
            >
              <option value="">{t('customer.pricing_tier.none')}</option>
              {tiers
                .filter((tier) =>
                  partner.billingMode === 'FIXED_MONTHLY'
                    ? tier.kind === 'FIXED_MONTHLY'
                    : tier.kind === 'PER_DAY',
                )
                .map((tier) => (
                  <option key={tier.id} value={tier.id}>
                    {tier.name}
                  </option>
                ))}
            </select>
            {assignError ? <div className="mt-1 text-xs text-red-600">{assignError}</div> : null}
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              {t('customer.col.paid_until')}
            </div>
            <div className="mt-0.5">
              <span
                className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${
                  TONE_CLASS[paidUntilTone(partner.paidUntil, settings.paidUntilWarningDays)]
                }`}
              >
                {partner.paidUntil ? formatDate(partner.paidUntil) : t('customer.paid_until.never')}
              </span>
            </div>
          </div>
          <Info label={t('customer.col.last_payment')}>{formatDate(partner.lastPaymentAt)}</Info>
        </div>

        <BillingAccountCard owner="partners" ownerId={partner.id} reloadKey={reloadKey} />

        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
            {t('payment.list.title')}
          </div>
          {payments.length === 0 ? (
            <div className="text-sm text-muted-foreground">{t('payment.list.empty')}</div>
          ) : (
            <ul className="divide-y divide-border rounded border border-border text-sm">
              {payments.map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-2 px-3 py-1.5">
                  <span className="text-muted-foreground">{formatDate(p.paidAt)}</span>
                  <span className="flex items-center gap-3">
                    {p.reference ? (
                      <span className="text-xs text-muted-foreground">{p.reference}</span>
                    ) : null}
                    <span className="font-medium tabular-nums">
                      {formatCents(p.amountCents, p.currency)}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <StockMovementsTable partnerId={partner.id} reloadKey={reloadKey} pageSize={40} />

      {skuOpen ? (
        <CreateSkuDialog
          partnerId={partner.id}
          onClose={() => setSkuOpen(false)}
          onCreated={() => {
            setSkuOpen(false);
            setReloadKey((k) => k + 1);
          }}
        />
      ) : null}
      {configuratorOpen ? (
        <ProductConfigurator
          partnerId={partner.id}
          canCreate={canWriteSkus}
          onClose={() => {
            setConfiguratorOpen(false);
            setReloadKey((k) => k + 1);
          }}
        />
      ) : null}
      {stocktakeOpen ? (
        <StocktakeDialog
          partnerId={partner.id}
          onClose={() => {
            setStocktakeOpen(false);
            setReloadKey((k) => k + 1);
          }}
        />
      ) : null}
      {restockOpen ? (
        <RestockDialog
          partnerId={partner.id}
          onClose={() => setRestockOpen(false)}
          onRecorded={() => {
            setRestockOpen(false);
            setReloadKey((k) => k + 1);
          }}
        />
      ) : null}
      {adjustOpen ? (
        <AdjustStockDialog
          partnerId={partner.id}
          onClose={() => setAdjustOpen(false)}
          onRecorded={() => {
            setAdjustOpen(false);
            setReloadKey((k) => k + 1);
          }}
        />
      ) : null}
      {editOpen ? (
        <EditPartnerDialog
          partner={partner}
          onClose={() => setEditOpen(false)}
          onSaved={() => {
            setEditOpen(false);
            setReloadKey((k) => k + 1);
          }}
        />
      ) : null}
      {inviteOpen ? (
        <InvitePartnerUserDialog
          partnerId={partner.id}
          partnerName={partner.name}
          defaultEmail={partner.contactEmail}
          defaultName={partner.contactPerson}
          onClose={() => setInviteOpen(false)}
          onSent={() => {
            /* keep dialog open to show the confirmation */
          }}
        />
      ) : null}
      {paymentOpen ? (
        <RecordPaymentDialog
          apiBase={`/partners/${partner.id}`}
          onClose={() => setPaymentOpen(false)}
          onRecorded={() => {
            setPaymentOpen(false);
            setReloadKey((k) => k + 1);
          }}
        />
      ) : null}
      {billingOpen ? (
        <PartnerBillingDialog
          partnerId={partner.id}
          onClose={() => setBillingOpen(false)}
          onCharged={() => setReloadKey((k) => k + 1)}
        />
      ) : null}
    </section>
  );
}

function Info({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div>{children}</div>
    </div>
  );
}
